import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineCalendar,
  HiArrowPath,
  HiOutlineShoppingBag,
  HiXMark,
  HiMinus,
  HiPlus,
  HiOutlineCheckCircle,
  HiOutlineTicket,
  HiOutlineMapPin,
  HiOutlinePhone,
  HiOutlineExclamationCircle,
  HiChevronDown,
  HiOutlineBuildingOffice2, // Add this
  HiOutlineUser,
} from "react-icons/hi2";
import SummaryApi from "../common";
import toast from "react-hot-toast";
import PaymentModal from "./PaymentModal";
const FormInput = ({ label, value, onChange, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1 text-left">
      {label}
    </label>
    <input
      {...props}
      className="w-full bg-rose-50/50 border border-rose-100 rounded-xl p-3 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200 transition-all"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const MySubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [padBalance, setPadBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderLoading, setOrderLoading] = useState(false);
  const GRACE_PERIOD_DAYS = 7; // Allow renewal 5 days before expiry
  // Address States
  const [addresses, setAddresses] = useState([]);
  const [typeError, setTypeError] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    user_name: "",
    user_mobile: "",
    pincode: "",
    flat_number: "",
    street_name: "",
    land_mark: "",
    city: "",
    state: "",
    address: "",
    address_type: "",
  });

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [customPadsCount, setCustomPadsCount] = useState(0);
  const [renewLoading, setRenewLoading] = useState(null);

  // Renewal/Payment Modal States
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [renewingSubData, setRenewingSubData] = useState(null);
  // Check if the user has ANY plan that is currently active and not yet used up
  const hasAnyActivePlan = subscriptions.some(
    (sub) => sub.is_active === 1 && sub.is_used === 0,
  );

  const getUserData = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return {};
    try {
      return JSON.parse(sessionData);
    } catch (e) {
      return {};
    }
  }, []);

  const getUserId = useCallback(() => {
    const user = getUserData();
    return user.user_id || user.id || user;
  }, [getUserData]);
  // Validation for Address Type
  const handleTypeChange = (val) => {
    setAddressForm((prev) => ({ ...prev, address_type: val }));
    const isDuplicate = addresses.some(
      (addr) => addr.address_type?.toLowerCase() === val.toLowerCase(),
    );
    if (isDuplicate && val.trim() !== "") {
      setTypeError("address name is already used. Please choose another");
    } else {
      setTypeError("");
    }
  };
  const fetchTransactions = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      const res = await fetch(SummaryApi.subscription_traanstion_by_user.url, {
        method: SummaryApi.subscription_traanstion_by_user.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status && Array.isArray(result.data)) {
        setTransactions(result.data);
      }
    } catch (err) {
      console.error("Error fetching transactions", err);
    }
  }, [getUserId]);

  const fetchPadBalance = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      const res = await fetch(SummaryApi.pads_count_balance.url, {
        method: SummaryApi.pads_count_balance.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status && result.data) {
        setPadBalance(Number(result.data.pads_count));
      }
    } catch (err) {
      setPadBalance(0);
    }
  }, [getUserId]);

  const fetchUserAddresses = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      const res = await fetch(SummaryApi.getUserAddressbyId.url, {
        method: SummaryApi.getUserAddressbyId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status && Array.isArray(result.data)) {
        setAddresses(result.data);
        const def = result.data.find((a) => a.is_default === 1);
        if (def) setSelectedAddressId(def.address_id);
        else if (result.data.length > 0)
          setSelectedAddressId(result.data[0].address_id);
      }
    } catch (err) {
      console.error("Address error", err);
    }
  }, [getUserId]);

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    const userId = getUserId();
    try {
      const response = await fetch(SummaryApi.addressCreate.url, {
        method: SummaryApi.addressCreate.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...addressForm, user_id: userId }),
      });
      const result = await response.json();
      if (result.status) {
        showCustomToast(
          "Address Saved!",
          "Delivery location added successfully.",
        );
        await fetchUserAddresses();
        setShowAddressForm(false);
        setAddressForm({
          user_name: "",
          user_mobile: "",
          pincode: "",
          flat_number: "",
          street_name: "",
          land_mark: "",
          city: "",
          state: "",
          address: "",
          address_type: "",
        });
      }
    } catch (err) {
      toast.error("Could not save address");
    }
  };
  const checkSubscriptionExpiry = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    try {
      await fetch(SummaryApi.expiry_subscription_by_user.url, {
        method: SummaryApi.expiry_subscription_by_user.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      // Note: We don't necessarily need to set state here as this API usually
      // updates the status on the backend before we fetch the list below.
    } catch (err) {
      console.error("Error checking subscription expiry", err);
    }
  }, [getUserId]);
  const fetchSubscriptions = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(SummaryApi.getSubscriptionByUser.url, {
        method: SummaryApi.getSubscriptionByUser.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await response.json();
      if (result.status && Array.isArray(result.data))
        setSubscriptions(result.data);
    } catch (error) {
      showCustomToast("Error", "Could not load subscriptions", "error");
    } finally {
      setLoading(false);
    }
  }, [getUserId]);

  // --- NEW RENEWAL FLOW ---

  // 1. Prepare renewal and open modal
  const handleRenewClick = (sub) => {
    const currentEndDate = new Date(sub.package_end_date);

    // 1. Determine the duration to add
    // You can check by sub.plan_name or sub.plan_id
    let monthsToAdd = 1; // Default to 1 month

    const planName = sub.plan_name?.toLowerCase() || "";

    if (planName.includes("quarter") || planName.includes("3 month")) {
      monthsToAdd = 3;
    } else if (planName.includes("half") || planName.includes("6 month")) {
      monthsToAdd = 6;
    } else if (planName.includes("annual") || planName.includes("12 month")) {
      monthsToAdd = 12;
    }
    // Add other conditions based on your specific plan names/IDs

    // 2. Calculate the new End Date correctly
    const newEndDateObj = new Date(currentEndDate);
    newEndDateObj.setMonth(newEndDateObj.getMonth() + monthsToAdd);

    // Handle month-end overflow (e.g., Jan 31 + 1 month should be Feb 28/29)
    if (newEndDateObj.getDate() !== currentEndDate.getDate()) {
      newEndDateObj.setDate(0);
    }

    const newEndDateString = newEndDateObj.toISOString().split("T")[0];

    // 3. Prepare data for the PaymentModal
    setRenewingSubData({
      ...sub,
      name: sub.package_name,
      price: sub.price,
      padCount: `${sub.pads_count} Pads`,
      endDate: new Date(newEndDateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      rawNewEndDate: newEndDateString,
    });

    setIsPaymentModalOpen(true);
  };

  // 2. Execute renewal after payment simulation
  const executeRenewal = async (paymentMethod) => {
    const userId = getUserId();
    const sub = renewingSubData;
    const today = new Date().toISOString().split("T")[0];

    setRenewLoading(sub.user_pack_subscription_id);

    const payload = {
      user_pack_subscription_id: sub.user_pack_subscription_id,
      user_id: userId,
      plan_id: sub.plan_id,
      package_id: sub.package_id,
      package_end_date: sub.rawNewEndDate,
      next_billing_date: today,
      is_payment_success: 1,
      payment_mode: paymentMethod,
    };

    try {
      const response = await fetch(SummaryApi.subscription_renewal.url, {
        method: SummaryApi.subscription_renewal.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status || result.success) {
        showCustomToast(
          "Plan Renewed!",
          "Your package has been extended successfully.",
        );
        setIsPaymentModalOpen(false);
        await Promise.all([fetchSubscriptions(), fetchPadBalance()]);
      } else {
        // --- SPECIFIC ERROR CHECKING START ---
        if (result.message === "Invalid duplicate subscription") {
          showCustomToast(
            "Already Active",
            "You can only renew once the current pack expires.",
            "error",
          );
        } else {
          showCustomToast(
            "Renewal Failed",
            result.message || "Something went wrong",
            "error",
          );
        }
        // --- SPECIFIC ERROR CHECKING END ---

        setIsPaymentModalOpen(false); // Close the modal since the action is blocked
      }
    } catch (error) {
      console.error("Renewal Error:", error);
      toast.error("Error connecting to server");
    } finally {
      setRenewLoading(null);
    }
  };

  const handleRenewSubscription = async (sub) => {
    const userId = getUserId();
    if (!userId) return toast.error("User not found");

    // Calculate Dates
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD

    // Logic: Extend the package_end_date by adding 30 days (or calculate based on your plan)
    const currentEndDate = new Date(sub.package_end_date);
    const newEndDate = new Date(
      currentEndDate.setDate(currentEndDate.getDate() + 30),
    )
      .toISOString()
      .split("T")[0];

    setRenewLoading(sub.user_pack_subscription_id);

    const payload = {
      user_pack_subscription_id: sub.user_pack_subscription_id,
      user_id: userId,
      plan_id: sub.plan_id,
      package_id: sub.package_id,
      package_end_date: newEndDate,
      next_billing_date: today, // Required by your prompt
      is_payment_success: 1,
      payment_mode: "online",
    };

    try {
      const response = await fetch(SummaryApi.subscription_renewal.url, {
        method: SummaryApi.subscription_renewal.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status || result.success) {
        toast.success("Package renewed successfully!");
        // Refresh data to show new dates and balance
        await Promise.all([fetchSubscriptions(), fetchPadBalance()]);
      } else {
        toast.error(result.message || "Renewal failed");
      }
    } catch (error) {
      console.error("Renewal Error:", error);
      toast.error("Error connecting to server");
    } finally {
      setRenewLoading(null);
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    const loadInitialData = async () => {
      await checkSubscriptionExpiry();
      await Promise.all([
        fetchSubscriptions(),
        fetchUserAddresses(),
        fetchPadBalance(),
        fetchTransactions(),
      ]);
    };
    loadInitialData();
  }, [
    checkSubscriptionExpiry,
    fetchSubscriptions,
    fetchUserAddresses,
    fetchPadBalance,
    fetchTransactions,
  ]);

  const handleOpenModal = (subscription) => {
    setSelectedSub(subscription);

    // Set initial count based on the new logic
    if (padBalance >= 80) {
      setCustomPadsCount(Math.floor(padBalance / 2)); // Default to Half
    } else {
      setCustomPadsCount(padBalance); // Default to Full
    }

    setIsModalOpen(true);
  };
  const padOptions = [];
  if (padBalance >= 80) {
    // If more than 80, show Half and Full
    padOptions.push(Math.floor(padBalance / 2));
    padOptions.push(padBalance);
  } else if (padBalance > 0) {
    // If 80 or less, show only Full
    padOptions.push(padBalance);
  }

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSub(null);
    setShowAddressForm(false);
  };

  const handleCountChange = (value) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 1) setCustomPadsCount(1);
    else if (num > padBalance) {
      setCustomPadsCount(padBalance);
      showCustomToast(
        "Limit Reached",
        `Only ${padBalance} pads remaining in balance`,
        "error",
      );
    } else setCustomPadsCount(num);
  };

  const handleConfirmOrder = async () => {
    if (!selectedSub) return;
    if (!selectedAddressId)
      return toast.error("Please select a delivery address");
    if (customPadsCount <= 0) return toast.error("Quantity must be at least 1");

    const userId = getUserId();
    const selectedAddress = addresses.find(
      (addr) => addr.address_id === selectedAddressId,
    );

    setOrderLoading(true);
    try {
      const orderRes = await fetch(SummaryApi.subscriptionOrderCreate.url, {
        method: SummaryApi.subscriptionOrderCreate.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          user_name: selectedAddress.user_name,
          mobile_number: selectedAddress.user_mobile,
          delivery_charges: 0,
          total_amount: 0,
          is_subscription: 1,
          user_pack_subscription_id: selectedSub.user_pack_subscription_id,
          pads_count: customPadsCount,
          order_address: `${selectedAddress.flat_number}, ${selectedAddress.street_name}, ${selectedAddress.land_mark}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pincode}`,
        }),
      });

      const orderResult = await orderRes.json();
      if (orderResult.success || orderResult.status === true) {
        const withdrawRes = await fetch(SummaryApi.Order_withdraw_pads.url, {
          method: SummaryApi.Order_withdraw_pads.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            withdraw_pads_count: customPadsCount,
          }),
        });
        const withdrawResult = await withdrawRes.json();
        if (withdrawResult.status || withdrawResult.success) {
          showCustomToast(
            "Order Placed!",
            `Request for ${customPadsCount} pads confirmed.`,
          );
          await fetchPadBalance();
          await fetchTransactions();
          handleCloseModal();
        } else {
          showCustomToast(
            "Balance Error",
            "Order placed, but balance update failed",
            "error",
          );
        }
      } else {
        showCustomToast(
          "Order Failed",
          orderResult.message || "Could not place order",
          "error",
        );
      }
    } catch (error) {
      showCustomToast("Error", "Processing order failed", "error");
    } finally {
      setOrderLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };
  const showCustomToast = (title, message, type = "success") => {
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-white shadow-2xl rounded-[1.8rem] pointer-events-auto flex ring-1 ring-black ring-opacity-5 border-b-[6px] ${
            type === "success" ? "border-rose-500" : "border-amber-500"
          }`}
        >
          <div className="flex-1 w-0 p-5">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div
                  className={`h-12 w-12 rounded-2xl flex items-center justify-center ${
                    type === "success"
                      ? "bg-rose-100 text-rose-500"
                      : "bg-amber-100 text-amber-500"
                  }`}
                >
                  {type === "success" ? (
                    <HiOutlineCheckCircle size={28} />
                  ) : (
                    <HiOutlineExclamationCircle size={28} />
                  )}
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-base font-black text-rose-950 leading-tight">
                  {title}
                </p>
                <p className="mt-1 text-sm font-bold text-rose-400/80">
                  {message}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center pr-4">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-4 py-2 text-sm font-black text-rose-600 hover:text-rose-800 transition-colors uppercase tracking-wider"
            >
              Close
            </button>
          </div>
        </div>
      ),
      { duration: 2000 },
    );
  };
  if (loading)
    return (
      <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-rose-500"></div>
      </div>
    );
  // 1. Sort to find latest
  const latestSub =
    subscriptions.length > 0
      ? [...subscriptions].sort(
          (a, b) => b.user_pack_subscription_id - a.user_pack_subscription_id,
        )[0]
      : null;

  // 2. Total calculation
  const totalPadsEver = subscriptions.reduce(
    (sum, s) => sum + Number(s.pads_count),
    0,
  );
  const totalWithdrawnEver = transactions.reduce(
    (sum, t) => sum + Number(t.withdraw_pads_count),
    0,
  );
  const cumulativePending = totalPadsEver - totalWithdrawnEver;

  // 3. Calculate Carry Forward: (Total Pending) - (Latest Pack's Initial Count)
  // We use Math.max(0, ...) to ensure we don't show negative numbers if they started using current pack pads
  const carryForwardPads = latestSub
    ? Math.max(0, cumulativePending - Number(latestSub.pads_count))
    : 0;
  // --- GROUPING LOGIC ---
  // 1. Create a unique list of Plan+Package combinations
  const groupedSubs = subscriptions.reduce((acc, sub) => {
    const key = `${sub.plan_id}-${sub.package_id}`;

    // If we haven't seen this plan/package combo, or this one is newer (higher ID), store it
    if (
      !acc[key] ||
      sub.user_pack_subscription_id > acc[key].user_pack_subscription_id
    ) {
      acc[key] = { ...sub };
    }
    return acc;
  }, {});

  // Convert the object back into an array for mapping
  const displaySubscriptions = Object.values(groupedSubs);

  // Helper function to calculate stats for a specific group
  const getGroupStats = (planId, packageId, currentPadsCount) => {
    // Find all subscriptions belonging to this specific Plan/Package combo
    const relatedSubs = subscriptions.filter(
      (s) => s.plan_id === planId && s.package_id === packageId,
    );

    // Sum all pads ever purchased for this combo
    const totalGroupPads = relatedSubs.reduce(
      (sum, s) => sum + Number(s.pads_count),
      0,
    );

    // Get all transaction IDs for these specific subscriptions
    const subIds = relatedSubs.map((s) => Number(s.user_pack_subscription_id));

    // Sum all withdrawals made against these specific subscriptions
    const totalGroupWithdrawn = transactions
      .filter((t) => subIds.includes(Number(t.user_pack_subscription_id)))
      .reduce((sum, t) => sum + Number(t.withdraw_pads_count), 0);

    const pending = totalGroupPads - totalGroupWithdrawn;
    const carryForward = Math.max(0, pending - Number(currentPadsCount));

    return { pending, carryForward };
  };

  return (
    <div className="min-h-screen bg-[#FFF0F3] py-14 px-6">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 bg-rose-500 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-rose-200">
              <HiArrowPath size={28} />
            </div>
            <div>
              <h1 className="text-4xl font-black text-rose-950 uppercase tracking-tight">
                My Subscriptions
              </h1>
              <p className="text-rose-400 font-bold text-sm mt-1 uppercase tracking-widest">
                Manage your cycle plans
              </p>
            </div>
          </div>

          <div className="bg-white px-8 py-4 rounded-[2rem] shadow-lg border-2 border-white flex items-center gap-4">
            <div className="h-12 w-12 bg-rose-100 rounded-xl flex items-center justify-center text-rose-500">
              <HiOutlineTicket size={28} />
            </div>
            <div>
              <p className="text-[10px] font-black text-rose-900/40 uppercase">
                Pads Balance
              </p>
              <p className="text-2xl font-black text-rose-600 leading-none">
                {padBalance} <span className="text-sm">Pads</span>
              </p>
            </div>
          </div>
        </div>

        {/* LIST */}
        <div className="space-y-8">
          {displaySubscriptions.length > 0 ? (
            displaySubscriptions.map((sub, index) => {
              // Calculate stats for this specific Plan/Package combo
              const { pending, carryForward } = getGroupStats(
                sub.plan_id,
                sub.package_id,
                sub.pads_count,
              );

              // 2. Logic to check if the plan is still active/valid
              // Plan is considered "Still Active" if it's not expired AND has pads left
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const expiryDate = new Date(sub.package_end_date);
              const gracePeriodStartDate = new Date(expiryDate);
              gracePeriodStartDate.setDate(
                gracePeriodStartDate.getDate() - GRACE_PERIOD_DAYS,
              );
              const isExpired = sub.is_expired === 1 || expiryDate < today;
              const isWithinGracePeriod = today >= gracePeriodStartDate;
              const canRenewThisSpecificSub = isExpired || isWithinGracePeriod;
              const renewalAllowedDate = new Date(expiryDate);
              renewalAllowedDate.setDate(renewalAllowedDate.getDate() + 7);

              // Condition: Is today at least 7 days after the expiry?
              const isAfterGracePeriod = today >= renewalAllowedDate;

              // The button is enabled ONLY if there are no other active plans
              // AND the specific sub has reached End Date + 7 days
              const canRenew = !hasAnyActivePlan && isAfterGracePeriod;

              // Calculate days remaining until renewal is allowed (for the tooltip)
              const diffTime = renewalAllowedDate - today;
              const daysUntilAllowed = Math.ceil(
                diffTime / (1000 * 60 * 60 * 24),
              );
              const hasPadsLeft = pending > 0;

              const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              const isCorporate = sub.organization_id > 0;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  key={sub.user_pack_subscription_id}
                  className="bg-white/70 backdrop-blur-md border border-white rounded-[2.5rem] overflow-hidden shadow-xl"
                >
                  {/* Header Section */}
                  <div className="p-6 md:p-8 border-b border-rose-50 bg-white/40 flex flex-wrap justify-between items-center gap-4">
                    <div className="flex gap-6 flex-wrap">
                      <div>
                        <p className="text-[10px] font-black text-rose-900/40 uppercase mb-1">
                          Started On
                        </p>
                        <div className="flex items-center gap-1 text-sm font-bold text-rose-950">
                          <HiOutlineCalendar className="text-rose-400" />
                          {formatDate(sub.package_start_date)}
                        </div>
                      </div>

                      <div className="flex flex-col">
                        <p className="text-[10px] font-black text-rose-900/40 uppercase mb-1">
                          Subscription Source
                        </p>
                        {isCorporate ? (
                          <div className="flex items-center gap-1.5 bg-sky-50 text-sky-700 px-3 py-1 rounded-full border border-sky-100 shadow-sm">
                            <HiOutlineBuildingOffice2
                              size={14}
                              className="text-sky-500"
                            />
                            <span className="text-[10px] font-black uppercase tracking-wider">
                              Corporate: {sub.organization_name}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 bg-rose-50 text-rose-600 px-3 py-1 rounded-full border border-rose-100 shadow-sm">
                            <HiOutlineUser
                              size={14}
                              className="text-rose-400"
                            />
                            <span className="text-[10px] font-black uppercase tracking-wider">
                              Personal / Online
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          sub.is_expired === 1
                            ? "bg-gray-200 text-gray-700"
                            : sub.is_used === 1
                              ? "bg-amber-100 text-amber-700"
                              : sub.is_active === 1
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {sub.is_expired === 1
                          ? "PACK EXPIRED"
                          : sub.is_used === 1
                            ? "USED"
                            : sub.is_active === 1
                              ? "ACTIVE PLAN"
                              : "INACTIVE"}
                      </span>

                      <div className="text-right">
                        <p className="text-[10px] font-black text-rose-900/40 uppercase mb-1">
                          Amount
                        </p>
                        <p className="text-xl font-black text-rose-500">
                          ₹{sub.price}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sub Info */}
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Package
                        </p>
                        <p className="text-xl font-black text-rose-950">
                          {sub.package_name || "—"}
                        </p>
                      </div>
                      <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Plan
                        </p>
                        <p className="text-xl font-black text-rose-950">
                          {sub.plan_name || "—"}
                        </p>
                      </div>
                      <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Pack Size
                        </p>
                        <p className="text-2xl font-black text-rose-950">
                          {sub.pads_count}
                        </p>
                      </div>
                    </div>

                    {/* Second Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {/* <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Discount
                        </p>
                        <p className="text-xl font-bold text-rose-950">
                          {sub.discount}%
                        </p>
                      </div> */}
                      <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Ends On
                        </p>
                        <p className="text-xl font-bold text-rose-950">
                          {formatDate(sub.package_end_date)}
                        </p>
                      </div>
                      {/* <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Next Billing
                        </p>
                        <p className="text-xl font-bold text-rose-950">
                          {sub.next_billing_date
                            ? formatDate(sub.next_billing_date)
                            : "No renewal"}
                        </p>
                      </div> */}
                      <div className="bg-rose-50/60 rounded-2xl p-5">
                        <p className="text-xs font-black text-rose-900/60 uppercase mb-1">
                          Payment
                        </p>
                        <p className="text-xl font-bold text-rose-950 uppercase">
                          {sub.payment_mode}
                        </p>
                      </div>
                      {/* PENDING PADS FOR THIS SPECIFIC PACKAGE COMBO */}
                      <div className="bg-rose-500 rounded-2xl p-5 text-white shadow-lg shadow-rose-200">
                        <p className="text-xs font-black uppercase mb-1 opacity-80">
                          Pending Pads
                        </p>
                        <p className="text-3xl font-black">
                          {Math.max(0, Math.floor(pending))}
                        </p>
                        <p className="text-[9px] font-bold mt-1 opacity-70 leading-tight">
                          (Includes {Math.floor(carryForward)} pads
                          carry-forward from previous renewals)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-8 py-6 bg-rose-50/50 border-t border-white flex flex-col sm:flex-row justify-between items-center gap-6">
                    <button
                      // DISABLED IF:
                      // 1. Another renewal is processing
                      // 2. OR it hasn't reached End Date + 7 days
                      // 3. OR there is another active plan globally
                      disabled={
                        renewLoading === sub.user_pack_subscription_id ||
                        !canRenew
                      }
                      onClick={() => handleRenewClick(sub)}
                      className={`text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-1.5 ${
                        renewLoading === sub.user_pack_subscription_id ||
                        !canRenew
                          ? "text-gray-400 cursor-not-allowed opacity-50"
                          : "text-rose-600 hover:text-rose-800"
                      }`}
                      title={
                        hasAnyActivePlan
                          ? "You already have an active plan"
                          : !isAfterGracePeriod
                            ? `Renewal available in ${daysUntilAllowed} days (Required: End Date + 7 days)`
                            : ""
                      }
                    >
                      <HiArrowPath
                        className={
                          renewLoading === sub.user_pack_subscription_id
                            ? "animate-spin"
                            : ""
                        }
                        size={14}
                      />
                      {renewLoading === sub.user_pack_subscription_id
                        ? "Processing..."
                        : "Renew Plan"}
                    </button>
                    <button
                      disabled={!sub.is_active || pending <= 0}
                      onClick={() => handleOpenModal(sub)}
                      className={`flex items-center gap-2 px-8 py-3 rounded-xl font-black uppercase tracking-wider text-sm transition-all ${
                        sub.is_active && pending > 0
                          ? "bg-rose-500 text-white hover:bg-rose-600"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      <HiOutlineShoppingBag size={18} /> Place Order
                    </button>
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="text-center py-20 bg-white/40 rounded-[2.5rem] border-2 border-dashed border-rose-200">
              <p className="text-rose-950 font-bold uppercase tracking-widest">
                No Subscriptions Found
              </p>
            </div>
          )}
        </div>
      </div>

      {/* MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseModal}
              className="absolute inset-0 bg-rose-950/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              className="relative bg-white w-full max-w-lg rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-8 md:p-10 overflow-y-auto">
                <div className="flex justify-between items-center mb-8">
                  <h3 className="text-2xl font-black text-rose-950 uppercase">
                    Withdraw Pads
                  </h3>
                  <button onClick={handleCloseModal} className="text-rose-950">
                    <HiXMark size={24} />
                  </button>
                </div>

                {/* Counter Section */}
                {!showAddressForm && (
                  <div className="bg-rose-50 rounded-[2rem] p-6 mb-8 text-center">
                    <p className="text-[10px] font-black text-rose-400 uppercase mb-4">
                      Quantity to Ship
                    </p>
                    {/* <div className="flex items-center justify-center gap-8">
                      <button
                        onClick={() => handleCountChange(customPadsCount - 1)}
                        className="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-rose-500 shadow-sm"
                      >
                        <HiMinus />
                      </button>
                      <span className="text-4xl font-black text-rose-950">
                        {customPadsCount}
                      </span>
                      <button
                        onClick={() => handleCountChange(customPadsCount + 1)}
                        className="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-rose-500 shadow-sm"
                      >
                        <HiPlus />
                      </button>
                    </div> */}
                    <div className="flex flex-col items-center justify-center">
                      {padBalance > 0 ? (
                        <div className="relative w-full max-w-[200px]">
                          <select
                            value={customPadsCount}
                            onChange={(e) =>
                              setCustomPadsCount(Number(e.target.value))
                            }
                            className="h-12 w-full pl-5 pr-10 rounded-xl bg-white border-2 border-rose-100 text-rose-950 font-black shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-400 appearance-none cursor-pointer transition-all hover:border-rose-300"
                          >
                            {padOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}{" "}
                                {opt === padBalance ? "(Max Balance)" : "Pads"}
                              </option>
                            ))}
                          </select>

                          {/* Down Arrow Icon */}
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <HiChevronDown className="h-5 w-5 text-rose-500 stroke-[3]" />
                          </div>
                        </div>
                      ) : (
                        <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 flex items-center gap-2">
                          <HiOutlineExclamationCircle className="text-amber-500" />
                          <p className="text-amber-700 text-xs font-bold uppercase">
                            No pads available in balance
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Address Section */}
                {!showAddressForm ? (
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-rose-950 uppercase tracking-widest">
                      Select Address
                    </h4>
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                      {addresses.map((addr) => (
                        <div
                          key={addr.address_id}
                          onClick={() => setSelectedAddressId(addr.address_id)}
                          className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-start gap-4 ${selectedAddressId === addr.address_id ? "border-rose-500 bg-rose-50/50" : "border-gray-100"}`}
                        >
                          <div
                            className={`mt-1 ${selectedAddressId === addr.address_id ? "text-rose-500" : "text-gray-300"}`}
                          >
                            {selectedAddressId === addr.address_id ? (
                              <HiOutlineCheckCircle size={24} />
                            ) : (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-100" />
                            )}
                          </div>
                          <div>
                            <p className="font-black text-rose-950 uppercase text-xs">
                              {addr.user_name}
                            </p>
                            <p className="text-[10px] text-rose-900/60 uppercase">
                              {addr.flat_number}, {addr.city}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowAddressForm(true)}
                      className="w-full py-3 border-2 border-dashed border-rose-200 rounded-2xl text-rose-500 font-black text-xs uppercase"
                    >
                      + Add New Address
                    </button>
                    <button
                      disabled={orderLoading || !selectedAddressId}
                      onClick={handleConfirmOrder}
                      className="w-full bg-rose-500 text-white py-4 rounded-2xl font-black uppercase tracking-widest shadow-lg mt-4"
                    >
                      {orderLoading ? "Processing..." : "Confirm Shipment"}
                    </button>
                  </div>
                ) : (
                  /* Address Form */
                  <form
                    onSubmit={handleAddressSubmit}
                    className="space-y-4 bg-rose-50/20 p-6 rounded-[2.5rem] border border-rose-100"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-black text-rose-950 uppercase">
                        New Address Details
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddressForm(false);
                          setTypeError("");
                        }}
                        className="text-rose-400 text-[10px] font-black uppercase underline"
                      >
                        Back
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormInput
                        label="Full Name"
                        value={addressForm.user_name}
                        onChange={(v) =>
                          setAddressForm({ ...addressForm, user_name: v })
                        }
                        required
                      />
                      <FormInput
                        label="Mobile"
                        maxLength={10}
                        value={addressForm.user_mobile}
                        onChange={(v) =>
                          setAddressForm({
                            ...addressForm,
                            user_mobile: v.replace(/\D/g, ""),
                          })
                        }
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormInput
                        label="Flat / House No"
                        value={addressForm.flat_number}
                        onChange={(v) =>
                          setAddressForm({ ...addressForm, flat_number: v })
                        }
                        required
                      />
                      <FormInput
                        label="Street Name"
                        value={addressForm.street_name}
                        onChange={(v) =>
                          setAddressForm({ ...addressForm, street_name: v })
                        }
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormInput
                        label="Landmark"
                        value={addressForm.land_mark}
                        onChange={(v) =>
                          setAddressForm({ ...addressForm, land_mark: v })
                        }
                      />
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">
                          Type (e.g. Home, Work)
                        </label>
                        <input
                          className={`w-full bg-rose-50/50 border ${typeError ? "border-red-400" : "border-rose-100"} rounded-xl p-3 text-sm text-rose-950 font-bold outline-none focus:ring-2 ring-rose-200 transition-all`}
                          placeholder="Enter address name"
                          value={addressForm.address_type}
                          onChange={(e) => handleTypeChange(e.target.value)}
                          required
                        />
                        {typeError && (
                          <span className="text-[9px] text-red-500 font-bold ml-1">
                            {typeError}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <FormInput
                        label="City"
                        value={addressForm.city}
                        onChange={(v) =>
                          setAddressForm({ ...addressForm, city: v })
                        }
                        required
                      />
                      <FormInput
                        label="State"
                        value={addressForm.state}
                        onChange={(v) =>
                          setAddressForm({ ...addressForm, state: v })
                        }
                        required
                      />
                      <FormInput
                        label="Pincode"
                        maxLength={6}
                        value={addressForm.pincode}
                        onChange={(v) =>
                          setAddressForm({
                            ...addressForm,
                            pincode: v.replace(/\D/g, ""),
                          })
                        }
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-black text-rose-400 uppercase tracking-widest ml-1">
                        Full Address
                      </label>
                      <textarea
                        rows="2"
                        className="w-full bg-rose-50/50 border border-rose-100 rounded-xl p-3 text-sm text-rose-950 font-bold outline-none"
                        value={addressForm.address}
                        onChange={(e) =>
                          setAddressForm({
                            ...addressForm,
                            address: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!!typeError}
                      className={`w-full ${typeError ? "bg-gray-300" : "bg-rose-600 hover:bg-rose-700"} text-white py-4 rounded-xl font-black uppercase text-xs tracking-widest shadow-lg mt-2 transition-all`}
                    >
                      Save Address
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* RENEWAL PAYMENT MODAL */}
      {renewingSubData && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          packageData={renewingSubData}
          onPaymentSuccess={(method) => executeRenewal(method)}
        />
      )}
    </div>
  );
};

export default MySubscriptions;
